import uniqBy from 'lodash/uniqBy';
import { useEffect, useCallback, useState } from 'react';
import { parseISO } from 'date-fns';
import useSlingApi from '@sdk/api/useSlingApi';
import useToast from '@sdk/common/toast/useToast';
import getErrorForToast from '@sdk/api/utils/getErrorForToast';
import useAuth from '@sdk/authentication/useAuth';
import { EventActionType, ActivityLogScheduleFilters } from '@sdk/shifts/types/ActivityLogType';
import { usePersonaCollection } from '@sdk/personaCollection/PersonaCollectionContext';
import type {
  ActivityLogItemType,
  ActivityLogItemFromApi,
} from '@sdk/shifts/types/ActivityLogType';

import {
  NO_GROUP_FILTER_OPTION_ID,
  NO_TAG_FILTER_OPTION_ID,
  OTHER_DAY_PART_FILTER_OPTION_ID,
} from '@angular/shifts/constants';

const fillScheduleFilters = (
  activityLogScheduleFilters: ActivityLogScheduleFilters,
  scheduleFilters,
  personaCollection,
  dayParts,
  dateRange
): void => {
  if (scheduleFilters.locationIds?.length) {
    activityLogScheduleFilters.location_ids = scheduleFilters.locationIds;
  }

  if (scheduleFilters.positionIds?.length) {
    activityLogScheduleFilters.position_ids = scheduleFilters.positionIds;
  }

  if (scheduleFilters.userIds?.length) {
    activityLogScheduleFilters.user_ids = scheduleFilters.userIds;
  }

  if (scheduleFilters.groupIds?.length) {
    const includeGroupIds = scheduleFilters.groupIds.filter(
      (id) => id !== NO_GROUP_FILTER_OPTION_ID
    );
    activityLogScheduleFilters.group_ids = includeGroupIds;

    if (scheduleFilters.groupIds.find((id) => id === NO_GROUP_FILTER_OPTION_ID)) {
      activityLogScheduleFilters.exclude_group_ids = personaCollection.groups
        .filter((g) => !includeGroupIds.includes(g.id))
        .map((g) => g.id);
    }
  }

  if (scheduleFilters.tagIds?.length) {
    const includeTagIds = scheduleFilters.tagIds.filter((id) => id !== NO_TAG_FILTER_OPTION_ID);
    activityLogScheduleFilters.tag_ids = includeTagIds;

    if (scheduleFilters.tagIds.find((id) => id === NO_TAG_FILTER_OPTION_ID)) {
      activityLogScheduleFilters.exclude_tag_ids = personaCollection.tags
        .filter((t) => !includeTagIds.includes(t.id))
        .map((t) => t.id);
    }
  }

  if (scheduleFilters.dayPartIds?.length) {
    const includeDayPartIds = scheduleFilters.dayPartIds.filter(
      (id) => id !== OTHER_DAY_PART_FILTER_OPTION_ID
    );
    activityLogScheduleFilters.day_part_ids = includeDayPartIds;

    if (scheduleFilters.dayPartIds.find((id) => id === OTHER_DAY_PART_FILTER_OPTION_ID)) {
      activityLogScheduleFilters.exclude_day_part_ids = dayParts
        .filter((d) => !includeDayPartIds.includes(d.id))
        .map((d) => d.id);
    }
  }

  if (scheduleFilters.eventTypes) {
    activityLogScheduleFilters.event_types = scheduleFilters.eventTypes;
  }

  activityLogScheduleFilters.dates = `${dateRange[0]}/${dateRange[dateRange.length - 1]}`;
};

export const useActivityLog = ({
  open,
  isMySchedule,
  isMyLocations,
  isAllLocations,
  isExperimental,
  reloadAt,
  scheduleFilters,
  dayParts,
  dateRange,
}) => {
  const api = useSlingApi();
  const { currentUser } = useAuth();
  const { showErrorToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [applyScheduleFilters, setApplyScheduleFilters] = useState(
    currentUser.settings.clientFlags.applyScheduleFiltersOnActivityLog ?? false
  );
  const [activityData, setActivityData] = useState([]);
  const [areAllPastLogsLoaded, setAreAllPastLogsLoaded] = useState(false);
  const [isInitialLoaded, setIsInitialLoaded] = useState(false);
  const personaCollection = usePersonaCollection();

  const filterEvents = useCallback(
    (item: ActivityLogItemFromApi) => {
      if (isMySchedule) {
        return item.items.some((event) => event.user?.id === currentUser.id);
      }

      if (
        isMyLocations ||
        (isExperimental && !currentUser.orgSettings.managersCanSeeSchedulesInOtherLocations)
      ) {
        return item.items.some((event) => currentUser.locationIds.includes(event.location?.id));
      }

      if (isAllLocations || isExperimental) {
        // ignore types not defined in our enum
        return Object.values(EventActionType).includes(item.type);
      }
    },
    [isMySchedule, isMyLocations, isAllLocations, isExperimental]
  );

  const mapEvents = useCallback(
    (item: ActivityLogItemFromApi) =>
      ({
        ...item,
        actor: {
          ...item.actor,
          fullname: currentUser.settings.showLastNameFirst
            ? `${item.actor.lastname} ${item.actor.name}`
            : `${item.actor.name} ${item.actor.lastname}`,
        },
        createdAt: parseISO(item.created_at),
      }) as ActivityLogItemType,
    []
  );

  const getActivityLog = useCallback(
    async (fromId: number = undefined) => {
      setIsLoading(true);

      try {
        const activityLogScheduleFilters: ActivityLogScheduleFilters = {};
        if (applyScheduleFilters && scheduleFilters) {
          fillScheduleFilters(
            activityLogScheduleFilters,
            scheduleFilters,
            personaCollection,
            dayParts,
            dateRange
          );
        }

        const { data } = await api.shifts.getActivityLog(fromId, activityLogScheduleFilters);
        if (!areAllPastLogsLoaded) {
          setAreAllPastLogsLoaded(Boolean(fromId && !data.length));
          if (data.length) {
            const lastId = data[data.length - 1].id;
            const pending = await api.shifts.getActivityLog(lastId, activityLogScheduleFilters);
            if (!pending.data.length) {
              setAreAllPastLogsLoaded(true);
            }
          }
        }
        const oldData = fromId ? activityData : [];
        const newData = data.filter(filterEvents).map(mapEvents);
        const sortedData = uniqBy(
          oldData.concat(newData).sort((a, b) => b.id - a.id),
          'id'
        );
        setActivityData(sortedData);
      } catch (e) {
        showErrorToast(getErrorForToast(e));
      } finally {
        setIsLoading(false);
      }
    },
    [
      isMySchedule,
      isMyLocations,
      isAllLocations,
      isExperimental,
      activityData,
      applyScheduleFilters,
      scheduleFilters,
      personaCollection,
      dayParts,
      dateRange,
    ]
  );

  useEffect(() => {
    if (open) {
      getActivityLog();
      setIsInitialLoaded(true);
    }
  }, [
    open,
    isMySchedule,
    isMyLocations,
    isAllLocations,
    isExperimental,
    reloadAt,
    applyScheduleFilters,
    scheduleFilters,
    dateRange,
  ]);

  const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (
      Math.round(e.currentTarget.scrollTop + e.currentTarget.clientHeight) >=
        e.currentTarget.scrollHeight - 1 &&
      !isLoading &&
      activityData.length &&
      !areAllPastLogsLoaded
    ) {
      getActivityLog(activityData[activityData.length - 1].id);
    }
  };

  return {
    isLoading,
    activityData,
    onScroll,
    areAllPastLogsLoaded,
    isInitialLoaded,
    applyScheduleFilters,
    setApplyScheduleFilters,
  };
};
